import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { loginSchema, type LoginFormData } from '../schemas/login.schema';
import { authService } from '@/services/auth.service';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/utils';
import { useReCaptchaV3 } from '@/components/ReCaptchaV3';
// import { SocialLoginButtons } from './SocialLoginButtons';

export function LoginForm() {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { executeRecaptcha } = useReCaptchaV3();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const rememberMe = watch('rememberMe');

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);

    try {
      // Execute reCAPTCHA v3
      const recaptchaToken = await executeRecaptcha('login');

      if (!recaptchaToken) {
        toast.error(t('messages.loginError'), {
          description: t('messages.recaptchaError'),
        });
        setIsLoading(false);
        return;
      }

      const { user, error } = await authService.login({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
        recaptchaToken,
      });

      if (error) {
        toast.error(t('messages.loginError'), {
          description: error.message,
        });
        return;
      }

      if (user) {
        toast.success(t('messages.loginSuccess'));

        // Navigation is handled by RedirectIfAuthenticated guard
        // The guard will wait for profile to load from AuthContext and redirect appropriately
        // No need to manually fetch profile or navigate here
      }
    } catch (error) {
      toast.error(t('messages.loginError'), {
        description: t('messages.unexpectedError'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col justify-center p-4 sm:p-6 md:p-12 lg:p-16">
      {/* Header */}
      <div className="mb-6 sm:mb-8 md:mb-10 text-center lg:text-left">
        <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-black text-slate-900 dark:text-slate-100 tracking-tight mb-1.5 sm:mb-2">
          {t('login.title', 'Welcome Back')}
        </h2>
        <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">
          {t('login.subtitle', 'Log in to continue your journey into the past.')}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4 md:space-y-5">
        {/* Email Field */}
        <div className="flex flex-col gap-2">
          <label
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
            htmlFor="email"
          >
            {t('login.email', 'Email Address')}
          </label>
          <div className="relative">
            <span className="material-symbols-outlined absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
              mail
            </span>
            <input
              className={cn(
                'w-full pl-9 sm:pl-11 md:pl-13 lg:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl border text-sm sm:text-base text-slate-900 dark:text-slate-100 transition-all placeholder:text-slate-400 placeholder:text-xs sm:placeholder:text-sm md:placeholder:text-base',
                'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
                'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
                errors.email && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              )}
              id="email"
              placeholder={t('login.emailPlaceholder', 'alex.smith@example.com')}
              type="email"
              disabled={isLoading}
              {...register('email')}
            />
          </div>
          {errors.email && (
            <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.email.message}</p>
          )}
        </div>

        {/* Password Field */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center px-1">
            <label
              className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300"
              htmlFor="password"
            >
              {t('login.password', 'Password')}
            </label>

            {/** Forget Password */}
            {/* <Link
              to={ROUTES.FORGOT_PASSWORD}
              className="text-xs font-bold text-primary hover:underline"
            >
              {t('login.forgotPassword', 'Forgot password?')}
            </Link> */}
          </div>
          <div className="relative flex items-center">
            <span className="material-symbols-outlined absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
              lock
            </span>
            <input
              className={cn(
                'w-full pl-9 sm:pl-11 md:pl-13 lg:pl-14 pr-11 sm:pr-12 md:pr-13 lg:pr-14 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl border text-sm sm:text-base text-slate-900 dark:text-slate-100 transition-all placeholder:text-slate-400 placeholder:text-xs sm:placeholder:text-sm md:placeholder:text-base',
                'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
                'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
                errors.password && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              )}
              id="password"
              placeholder={t('login.passwordPlaceholder', '••••••••')}
              type={showPassword ? 'text' : 'password'}
              disabled={isLoading}
              {...register('password')}
            />
            <button
              className="absolute right-2 sm:right-3 md:right-4 text-slate-400 hover:text-primary transition-colors min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('login.hidePassword', 'Hide password') : t('login.showPassword', 'Show password')}
            >
              <span className="material-symbols-outlined text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          {errors.password && (
            <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.password.message}</p>
          )}
        </div>

        {/* Remember Me Checkbox */}
        {/* <div className="flex items-center gap-2 px-1">
          <input
            className="appearance-none w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 cursor-pointer transition-all checked:bg-primary checked:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed relative checked:after:content-[''] checked:after:absolute checked:after:left-1/2 checked:after:top-1/2 checked:after:-translate-x-1/2 checked:after:-translate-y-1/2 checked:after:w-1.5 checked:after:h-1.5 checked:after:bg-white checked:after:rounded-full"
            id="remember"
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setValue('rememberMe', e.target.checked)}
            disabled={isLoading}
          />
          <label
            className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer"
            htmlFor="remember"
          >
            {t('login.rememberMe', 'Keep me logged in')}
          </label>
        </div> */}

        {/* Submit Button */}
        <button
          className="w-full bg-primary text-white font-bold py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-xs sm:text-sm md:text-base min-h-[44px]"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
              {t('login.submitting', 'Signing in...')}
            </>
          ) : (
            t('login.submit', 'Sign In to Family Tree')
          )}
        </button>

        {/* Social Login - Disabled until OAuth is implemented */}
        {/* <SocialLoginButtons /> */}
      </form>
    </div>
  );
}
